import { createFileRoute } from '@tanstack/react-router';

import type { VendorListItem } from '@keboola/api-client/developerPortal/types';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  PageLayout,
  Skeleton,
} from '@keboola/design';

import { useAuth } from '../../auth/context';
import { EditVendorModal } from '../../features/vendors/components/EditVendorModal';
import { MfaEnrolment } from '../../features/vendors/components/MfaEnrolment';
import { describeMfa } from '../../features/vendors/mfaStatus';
import { useAllVendors, useProfile } from '../../features/vendors/queries';
import { projectUrlSchema } from '../../features/vendors/schemas';

export const Route = createFileRoute('/~/profile')({
  component: VendorProfilePage,
  head: () => ({ meta: [{ title: 'Profile | Keboola Developer Portal' }] }),
});

function VendorProfilePage() {
  const { identity } = useAuth();
  const { data: profile, isLoading: profileLoading, isError: profileFailed } = useProfile();
  const {
    data: vendors,
    isLoading: vendorsLoading,
    isError: vendorsFailed,
    refetch: refetchVendors,
  } = useAllVendors();

  const mfa = describeMfa(profile?.mfa);

  // The vendors the user actually belongs to. Read from the public vendor list rather than a per-vendor
  // fetch: it is the only endpoint that carries a vendor's address and contact email, and the gate has
  // already loaded it.
  const myVendors = (vendors ?? []).filter(
    (vendor) => identity?.vendors.includes(vendor.id) === true,
  );

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Profile"
          description="Your account, multi-factor authentication, and vendors."
        />
      </PageLayout.Header>

      <div className="tw:grid tw:gap-6 tw:lg:grid-cols-2">
        <Card>
          <Card.Header>
            <Card.Title as="h2">Details</Card.Title>
          </Card.Header>
          <Card.Content className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-col">
              <span className="tw:font-medium">{identity?.name ?? '—'}</span>
              {identity?.email !== null &&
                identity?.email !== undefined && (
                  // `text-primary` explicitly: the design system's preflight is opt-in, so a bare `<a>`
                  // inherits body colour and reads as plain text.
                  <a href={`mailto:${identity.email}`} className="tw:text-sm tw:text-primary">
                    {identity.email}
                  </a>
                )}
            </div>

            <hr className="tw:border-border" />

            <div className="tw:flex tw:flex-col tw:items-start tw:gap-3">
              <p className="tw:m-0 tw:text-sm">
                Multi-factor authentication:{' '}
                {profileLoading ? (
                  <Skeleton className="tw:inline-block tw:h-4 tw:w-20 tw:align-middle" />
                ) : (
                  // `describeMfa` reports "Disabled" for an absent value, which is the right answer for
                  // a profile that says so and the wrong one for a profile that failed to load — the
                  // latter would tell an enrolled user they have no MFA.
                  <strong>{profileFailed ? 'Unknown' : mfa.label}</strong>
                )}
              </p>

              {profileFailed && (
                <Alert variant="error">
                  Your profile could not be loaded, so the state above is not reliable. Reload the
                  page to try again.
                </Alert>
              )}

              {mfa.isDeprecated && (
                <Alert variant="warning">
                  SMS-based verification is being retired. Set up an authenticator app to keep
                  access to your account.
                </Alert>
              )}

              <MfaEnrolment
                label={mfa.actionLabel}
                variant={mfa.isDeprecated ? 'primary' : 'secondary'}
              />
            </div>
          </Card.Content>
        </Card>

        <Card>
          <Card.Header>
            {/* `Card.Title as="h2"`, not a raw `<h2>`: `no-raw-heading-on-page` bans raw page-level
                headings outside a `PageHeader`, and this is a section under the page's own <h1>. */}
            <Card.Title as="h2">Vendors</Card.Title>
          </Card.Header>
          <Card.Content className="tw:flex tw:flex-col tw:gap-6">
            {vendorsLoading && <Skeleton className="tw:h-40" />}

            {vendorsFailed && (
              <ErrorState>
                <ErrorState.Title>Could not load your vendors</ErrorState.Title>
                <ErrorState.Actions>
                  <Button variant="secondary" onClick={() => void refetchVendors()}>
                    Try again
                  </Button>
                </ErrorState.Actions>
              </ErrorState>
            )}

            {vendors !== undefined && myVendors.length === 0 && (
              <EmptyState variant="compact">
                <EmptyState.Description>
                  You do not belong to any vendor yet.
                </EmptyState.Description>
              </EmptyState>
            )}

            {myVendors.map((vendor) => (
              <VendorTile key={vendor.id} vendor={vendor} />
            ))}
          </Card.Content>
        </Card>
      </div>
    </PageLayout>
  );
}

/**
 * One vendor's details. A bordered block rather than a nested `Card`: `bg-card` on a card inside a card
 * resolves to the same colour, so the inner one disappears in dark mode.
 */
function VendorTile({ vendor }: { vendor: VendorListItem }) {
  const projectUrl = projectUrlSchema.safeParse(vendor.projectUrl);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-border tw:pb-6 tw:text-sm tw:last:border-0 tw:last:pb-0">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
        <Card.Title as="h3">{vendor.name}</Card.Title>
        <EditVendorModal vendor={vendor} />
      </div>
      <div className="tw:flex tw:flex-col tw:gap-2">
        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:text-muted-foreground">Vendor ID</span>
          <code className="tw:font-mono">{vendor.id}</code>
        </div>
        {vendor.email !== undefined && vendor.email !== '' && (
          <a href={`mailto:${vendor.email}`} className="tw:text-primary">
            {vendor.email}
          </a>
        )}
        {vendor.address !== undefined &&
          vendor.address !== '' && (
            // `whitespace-pre-line` rather than splitting on `\n` into `<br>`s as the original did: the
            // address is stored with its line breaks and CSS is what preserves them.
            <p className="tw:m-0 tw:whitespace-pre-line tw:text-muted-foreground">
              {vendor.address}
            </p>
          )}
        {/* Re-checked here, not trusted from the form: `projectUrl` is a free-text column an admin can
            also set, and rows predate the schema's protocol rule — a stored `javascript:` URL would
            otherwise execute on click. An unrecognised value renders no link at all. */}
        {projectUrl.success && (
          <a
            href={projectUrl.data}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:text-primary"
          >
            Test project
          </a>
        )}
      </div>
    </div>
  );
}
